import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Stack,
  Typography, Chip, Divider, TextField, MenuItem, CircularProgress, Alert,
  Paper, IconButton, Tooltip, FormControl, InputLabel, Select,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import CallReceivedIcon from '@mui/icons-material/CallReceived';
import CallMadeIcon from '@mui/icons-material/CallMade';
import DOMPurify from 'dompurify';
import {
  getInteractionTimeline, processInboundManual, analyzeLinkedInReply, getAiProviders,
  improveEmail, sendEmail, generateLinkedInMessage, logSentLinkedInMessage,
} from '../services/api';

DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A') {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const sanitizeHtml = (raw) => DOMPurify.sanitize(raw, {
  USE_PROFILES: { html: true },
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'meta', 'link'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'formaction', 'srcdoc'],
  ALLOW_DATA_ATTR: false,
});

const CHANNELS = [
  { value: 'Email', label: 'Email', icon: <EmailOutlinedIcon fontSize="small" /> },
  { value: 'LinkedIn', label: 'LinkedIn', icon: <LinkedInIcon fontSize="small" sx={{ color: '#0a66c2' }} /> },
];

const TONES = ['Professional', 'Friendly', 'Direct', 'Casual'];
const LI_TYPES = [
  { value: 'ConnectionRequest', label: 'Connection request' },
  { value: 'FirstMessage', label: 'First message' },
  { value: 'FollowUp', label: 'Follow-up' },
];

export default function ContactConversationDialog({
  open, leadId, contact, onClose, onLogged,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [interactions, setInteractions] = useState([]);

  const [aiProviders, setAiProviders] = useState([]);
  const [aiProvider, setAiProvider] = useState('');

  const [direction, setDirection] = useState('Outbound');
  const [channel, setChannel] = useState('Email');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [tone, setTone] = useState('Professional');
  const [messageType, setMessageType] = useState('FirstMessage');
  const [maxLength, setMaxLength] = useState(4000);
  const [context, setContext] = useState('');

  const [analyzing, setAnalyzing] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [variants, setVariants] = useState([]);
  const [copied, setCopied] = useState(false);

  const hasEmail = !!contact?.email;
  const hasLinkedIn = !!contact?.linkedInUrl;

  const loadTimeline = useCallback(async () => {
    if (!leadId || !contact?.id) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await getInteractionTimeline(leadId);
      const items = (data?.items || data || []).filter((i) => i.contactId === contact.id);
      items.sort((a, b) => {
        const ta = new Date(a.interactionDate).getTime() || 0;
        const tb = new Date(b.interactionDate).getTime() || 0;
        return tb - ta;
      });
      setInteractions(items);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [leadId, contact?.id]);

  useEffect(() => {
    if (open) {
      const defaultCh = hasLinkedIn && !hasEmail ? 'LinkedIn' : 'Email';
      setChannel(defaultCh);
      setDirection('Outbound');
      setSubject('');
      setBody('');
      setContext('');
      setMessageType('FirstMessage');
      setMaxLength(defaultCh === 'LinkedIn' ? 200 : 4000);
      setAnalysis(null);
      setVariants([]);
      setCopied(false);
      setSuccess('');
      setError('');
      loadTimeline();
    }
  }, [open, hasEmail, hasLinkedIn, loadTimeline]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getAiProviders();
        const list = (data || []).filter((p) => p.isAvailable);
        setAiProviders(list);
        const def = list.find((p) => p.isDefault) || list[0];
        if (def) setAiProvider(def.name);
      } catch { /* ignore */ }
    })();
  }, []);

  const resetCompose = () => {
    setBody('');
    setSubject('');
    setContext('');
    setVariants([]);
    setAnalysis(null);
  };

  const handleAnalyzeInbound = async () => {
    if (!body.trim()) return;
    setAnalyzing(true);
    setError('');
    setSuccess('');
    setAnalysis(null);
    try {
      if (channel === 'LinkedIn') {
        const { data } = await analyzeLinkedInReply({
          leadId,
          contactId: contact.id,
          replyText: body,
          aiProviderName: aiProvider || undefined,
        });
        setAnalysis({
          intent: data?.intent,
          sentiment: data?.sentiment,
          summary: data?.summary,
          suggestedNextStep: data?.suggestedNextStep,
          suggestedReply: data?.suggestedReply,
          confidence: data?.confidenceScore,
          interactionId: data?.interactionId,
        });
      } else {
        const { data } = await processInboundManual({
          leadId,
          contactId: contact.id,
          fromEmail: contact.email || null,
          subject: subject || '(pasted reply)',
          body,
          channel: 'Email',
          analyzeWithAi: true,
          aiProviderName: aiProvider || undefined,
        });
        setAnalysis({
          intent: data?.aiIntent,
          summary: data?.aiSummary,
          suggestedNextStep: data?.aiSuggestedNextStep,
          interactionId: data?.id,
        });
      }
      setSuccess('Inbound logged and analyzed.');
      setBody('');
      setSubject('');
      await loadTimeline();
      onLogged?.();
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data || 'Failed to analyze');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAiHelper = async () => {
    setGenerating(true);
    setError('');
    setSuccess('');
    try {
      if (channel === 'LinkedIn') {
        const { data } = await generateLinkedInMessage({
          leadId,
          contactId: contact.id,
          messageType,
          tone,
          maxLength: Number(maxLength) > 0 ? Number(maxLength) : 200,
          context: context || undefined,
          aiProviderName: aiProvider || undefined,
        });
        setBody(data?.message || '');
        setVariants(data?.variants || []);
      } else {
        if (!body.trim()) {
          setError('Type a draft body first — Email AI helper improves existing text.');
          return;
        }
        const { data } = await improveEmail({
          subject: subject || '',
          body,
          tone,
          context: context || undefined,
          action: 'improve',
          aiProviderName: aiProvider || undefined,
        });
        if (data?.subject) setSubject(data.subject);
        if (data?.body) setBody(data.body);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data || 'AI helper failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendOutbound = async () => {
    if (!body.trim()) return;
    setSending(true);
    setError('');
    setSuccess('');
    try {
      if (channel === 'LinkedIn') {
        await logSentLinkedInMessage({
          leadId,
          contactId: contact.id,
          messageType,
          stage: 'FirstContact',
          body,
          notes: context || undefined,
        });
        setSuccess('Marked sent on LinkedIn and logged.');
      } else {
        await sendEmail({
          leadId,
          contactId: contact.id,
          subject: subject || '(no subject)',
          body,
          recipientEmail: contact.email || undefined,
        });
        setSuccess('Email sent.');
      }
      resetCompose();
      await loadTimeline();
      onLogged?.();
    } catch (e) {
      setError(e?.response?.data?.message || e?.response?.data || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const handleCopy = async (text) => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* ignore */ }
  };

  const fullName = useMemo(() =>
    `${contact?.firstName || ''} ${contact?.lastName || ''}`.trim() || '(no name)',
    [contact]);

  const formatDate = (iso) => iso ? new Date(iso).toLocaleString() : '';

  const looksLikeHtml = (s) => typeof s === 'string' && /<\/?[a-z][\s\S]*>/i.test(s);

  const suggestedReplyText = analysis?.suggestedReply || analysis?.suggestedNextStep || '';
  const isLinkedIn = channel === 'LinkedIn';
  const isInbound = direction === 'Inbound';
  const isOutbound = direction === 'Outbound';
  const overLimit = isLinkedIn && Number(maxLength) > 0 && body.length > Number(maxLength);
  const working = analyzing || generating || sending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Conversation — {fullName}
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {contact?.leadCompanyName || ''}{contact?.jobTitle ? ` · ${contact.jobTitle}` : ''}
        </Typography>
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ minHeight: 500 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{String(error)}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <ToggleButtonGroup
              value={direction}
              exclusive
              size="small"
              onChange={(_, v) => v && setDirection(v)}
            >
              <ToggleButton value="Inbound">
                <CallReceivedIcon fontSize="small" sx={{ mr: 0.5 }} /> Received
              </ToggleButton>
              <ToggleButton value="Outbound">
                <CallMadeIcon fontSize="small" sx={{ mr: 0.5 }} /> Send
              </ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ flex: 1 }} />
            <Typography variant="caption" color="text.secondary">
              {isInbound ? 'Log + AI-analyze a reply from contact' : 'Compose & send (or log) a message'}
            </Typography>
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Channel</InputLabel>
              <Select
                label="Channel"
                value={channel}
                onChange={(e) => {
                  const v = e.target.value;
                  setChannel(v);
                  setMaxLength(v === 'LinkedIn' ? (messageType === 'ConnectionRequest' ? 200 : 4000) : 4000);
                }}
              >
                {CHANNELS.map((c) => (
                  <MenuItem
                    key={c.value}
                    value={c.value}
                    disabled={(c.value === 'Email' && !hasEmail) || (c.value === 'LinkedIn' && !hasLinkedIn)}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">{c.icon}<span>{c.label}</span></Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isOutbound && (
              <TextField
                select
                size="small"
                label="Tone"
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                sx={{ minWidth: 140 }}
              >
                {TONES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
            )}

            {isOutbound && isLinkedIn && (
              <>
                <TextField
                  select
                  size="small"
                  label="Type"
                  value={messageType}
                  onChange={(e) => {
                    setMessageType(e.target.value);
                    if (e.target.value === 'ConnectionRequest') setMaxLength(200);
                    else if (Number(maxLength) <= 200) setMaxLength(4000);
                  }}
                  sx={{ minWidth: 180 }}
                >
                  {LI_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                </TextField>
                <TextField
                  size="small"
                  type="number"
                  label="Max chars"
                  value={maxLength}
                  onChange={(e) => setMaxLength(e.target.value)}
                  inputProps={{ min: 50, max: 8000, step: 10 }}
                  sx={{ minWidth: 110 }}
                />
              </>
            )}

            <TextField
              select
              size="small"
              label="AI provider"
              value={aiProvider}
              onChange={(e) => setAiProvider(e.target.value)}
              sx={{ minWidth: 160 }}
            >
              {aiProviders.length === 0 && <MenuItem value="">(default)</MenuItem>}
              {aiProviders.map((p) => (
                <MenuItem key={p.name} value={p.name}>
                  {p.displayName || p.name}{p.isDefault ? ' (default)' : ''}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {channel === 'Email' && (
            <TextField
              size="small"
              label="Subject"
              fullWidth
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          {isOutbound && (
            <TextField
              size="small"
              fullWidth
              multiline
              minRows={2}
              label="Optional context / angle"
              placeholder="e.g. saw they just raised Series B; reference our analytics use case"
              value={context}
              onChange={(e) => setContext(e.target.value)}
              sx={{ mb: 2 }}
            />
          )}

          <TextField
            size="small"
            fullWidth
            multiline
            minRows={6}
            label={isInbound ? 'Pasted reply' : (isLinkedIn ? 'Message body' : 'Email body')}
            placeholder={isInbound
              ? 'Paste the reply you received from the contact…'
              : (isLinkedIn ? 'Type, or click AI helper to generate…' : 'Type your email draft, then AI helper refines…')}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            error={overLimit}
            helperText={isLinkedIn
              ? `${body.length} / ${maxLength} chars${overLimit ? ' — over limit' : ''}`
              : `${body.length} chars`}
          />

          {variants.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Variants (click to use)
              </Typography>
              <Stack spacing={1}>
                {variants.map((v, i) => (
                  <Paper key={i} variant="outlined" sx={{ p: 1.5, cursor: 'pointer' }} onClick={() => setBody(v)}>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{v}</Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
            {isOutbound && (
              <Button
                variant="outlined"
                startIcon={generating ? <CircularProgress size={16} /> : <AutoFixHighIcon />}
                disabled={working}
                onClick={handleAiHelper}
              >
                {generating ? 'Working…' : isLinkedIn ? 'AI generate' : 'AI improve'}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              disabled={!body}
              onClick={() => handleCopy(body)}
            >
              {copied ? 'Copied!' : 'Copy'}
            </Button>
            <Box sx={{ flex: 1 }} />
            {isInbound ? (
              <Button
                variant="contained"
                startIcon={analyzing ? <CircularProgress size={16} color="inherit" /> : <AutoFixHighIcon />}
                disabled={working || !body.trim()}
                onClick={handleAnalyzeInbound}
              >
                {analyzing ? 'Analyzing…' : 'Log & analyze with AI'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                disabled={working || !body.trim() || overLimit
                  || (isLinkedIn ? !hasLinkedIn : !hasEmail)}
                onClick={handleSendOutbound}
              >
                {sending
                  ? (isLinkedIn ? 'Logging…' : 'Sending…')
                  : isLinkedIn ? 'Mark sent & log' : 'Send email'}
              </Button>
            )}
          </Stack>

          {isOutbound && isLinkedIn && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              LinkedIn has no send API. Copy, paste into LinkedIn manually, then "Mark sent & log".
            </Typography>
          )}

          {analysis && (
            <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'action.hover' }}>
              <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', rowGap: 0.5 }}>
                <Typography variant="overline" color="text.secondary">AI analysis</Typography>
                {analysis.intent && <Chip label={`Intent: ${analysis.intent}`} size="small" color="info" />}
                {analysis.sentiment && <Chip label={`Sentiment: ${analysis.sentiment}`} size="small" />}
                {typeof analysis.confidence === 'number' && (
                  <Chip label={`Confidence ${Math.round(analysis.confidence * 100)}%`} size="small" variant="outlined" />
                )}
              </Stack>
              {analysis.summary && (
                <>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>Summary</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>{analysis.summary}</Typography>
                </>
              )}
              {suggestedReplyText && (
                <>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>Suggested reply</Typography>
                    <Button size="small" startIcon={<ContentCopyIcon fontSize="small" />} onClick={() => handleCopy(suggestedReplyText)}>
                      Copy
                    </Button>
                    <Button
                      size="small"
                      onClick={() => {
                        setDirection('Outbound');
                        setBody(suggestedReplyText);
                        setAnalysis(null);
                      }}
                    >
                      Use as outbound draft ↑
                    </Button>
                  </Stack>
                  <TextField
                    size="small"
                    fullWidth
                    multiline
                    minRows={4}
                    value={suggestedReplyText}
                    InputProps={{ readOnly: true }}
                  />
                </>
              )}
            </Paper>
          )}
        </Paper>

        <Divider sx={{ my: 2 }}>
          <Chip label="History (newest first)" size="small" />
        </Divider>

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {interactions.length} interaction{interactions.length === 1 ? '' : 's'}
          </Typography>
          <Tooltip title="Reload">
            <IconButton size="small" onClick={loadTimeline} disabled={loading}><RefreshIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Stack>

        {loading ? (
          <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={28} /></Box>
        ) : interactions.length === 0 ? (
          <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No interactions yet for this contact.</Typography>
          </Paper>
        ) : (
          <Stack spacing={1.2}>
            {interactions.map((i) => (
              <Paper key={i.id} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5, flexWrap: 'wrap', rowGap: 0.5 }}>
                  <Chip label={i.channel} size="small" variant="outlined" />
                  <Chip
                    label={i.direction}
                    size="small"
                    color={i.direction === 'Inbound' ? 'success' : 'primary'}
                    variant="outlined"
                  />
                  <Chip label={i.status} size="small" variant="outlined" />
                  {i.aiIntent && <Chip label={i.aiIntent} size="small" color="info" variant="outlined" />}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                    {formatDate(i.interactionDate)}
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{i.subject || '(no subject)'}</Typography>
                {(i.bodyPreview || i.body) && (() => {
                  const raw = i.body || i.bodyPreview;
                  return looksLikeHtml(raw) ? (
                    <Box
                      sx={{
                        mt: 0.5,
                        maxHeight: 280,
                        overflowY: 'auto',
                        pr: 1,
                        fontSize: '0.85rem',
                        color: 'text.secondary',
                        '& img': { maxWidth: '100%' },
                        '& a': { wordBreak: 'break-all' },
                        '& blockquote': { borderLeft: '3px solid', borderColor: 'divider', pl: 1, ml: 0, color: 'text.disabled' },
                      }}
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(raw) }}
                    />
                  ) : (
                    <Box sx={{ mt: 0.5, maxHeight: 220, overflowY: 'auto', pr: 1 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                        {raw}
                      </Typography>
                    </Box>
                  );
                })()}
                {i.aiSummary && (
                  <Box sx={{ mt: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, maxHeight: 140, overflowY: 'auto' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>AI summary</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{i.aiSummary}</Typography>
                  </Box>
                )}
                {i.aiSuggestedNextStep && (
                  <Box sx={{ mt: 0.5, p: 1, border: '1px dashed', borderColor: 'divider', borderRadius: 1, maxHeight: 140, overflowY: 'auto' }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>Suggested next step</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{i.aiSuggestedNextStep}</Typography>
                  </Box>
                )}
              </Paper>
            ))}
          </Stack>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
